import React, { useEffect, useState } from "react";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core";
import StatTile, { formatCompact } from "../StatTile";
import WarningBanner from "../WarningBanner";
import HorizontalBarChart from "../charts/HorizontalBarChart";
import TimelinessCompareBars from "../charts/TimelinessCompareBars";
import { pick, pickActorLabel, genericColumns } from "../metricsAdapters";
import "../Analytics.css";

const useDashboardData = (fetcher, filters, refreshKey) => {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    fetcher(filters)
      .then((data) => {
        if (!cancelled) setState({ loading: false, data, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, data: null, error: err.message });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher, filters, refreshKey]);

  return state;
};

const deriveView = (config, { loading, data, error }) => {
  const { id, title, compareLabel, actorLabel, thresholdDays, avgFieldPaths, oldestFieldPaths } = config;
  const matrix = pick(data, ["performance_matrix", "matrix"], []) || [];

  const withinSlaPct = Number(pick(data, ["kpi_cards.within_sla_pct", "within_sla_pct"], 0)) || 0;
  const withinCount = Number(pick(data, ["kpi_cards.within_sla_count", "within_sla_count"], 0)) || 0;
  const outsideCount = Number(pick(data, ["kpi_cards.outside_sla_count", "outside_sla_count"], 0)) || 0;
  const avgDays = pick(data, avgFieldPaths.map((p) => `kpi_cards.${p}`).concat(avgFieldPaths), null);
  const oldestPendingDays = oldestFieldPaths
    ? pick(data, oldestFieldPaths.map((p) => `kpi_cards.${p}`).concat(oldestFieldPaths), null)
    : null;

  const rowLabel = (row) => pickActorLabel(row);
  const rowValue = (row) => pick(row, avgFieldPaths.concat(["avg_days", "days"]), 0);

  const chartItems = matrix.map((row, idx) => ({
    key: `${rowLabel(row)}-${idx}`,
    label: rowLabel(row),
    value: Number(rowValue(row)) || 0,
  }));

  const tableData = matrix.map((row, idx) => ({ ...row, _id: `${rowLabel(row)}-${idx}` }));
  const columns = genericColumns(tableData, { hide: ["_id"] });

  return {
    id,
    title,
    compareLabel,
    actorLabel,
    thresholdDays,
    oldestFieldPaths,
    loading,
    data,
    error,
    matrix,
    withinSlaPct,
    withinCount,
    outsideCount,
    avgDays,
    oldestPendingDays,
    chartItems,
    tableData,
    columns,
  };
};

// Approval / PO / Invoice Timeliness grouped under one shared header: an
// at-a-glance SLA%/count comparison across all 3 (whose rows double as tab
// triggers) plus an explicit tab strip, both driving one shared detail panel
// below — so only one dashboard's chart + table is ever on screen at once
// instead of all 3 stacked. `dashboards` is always exactly
// [approval, po, invoice], so all 3 hook calls below are unconditional and
// fixed in number, as React's rules of hooks require.
const TimelinessComparisonSection = ({ id, title, dashboards, filters, refreshKey }) => {
  const [d1, d2, d3] = dashboards;
  const s1 = useDashboardData(d1.fetcher, filters, refreshKey);
  const s2 = useDashboardData(d2.fetcher, filters, refreshKey);
  const s3 = useDashboardData(d3.fetcher, filters, refreshKey);
  const views = [deriveView(d1, s1), deriveView(d2, s2), deriveView(d3, s3)];

  const [activeId, setActiveId] = useState(views[0].id);
  const active = views.find((v) => v.id === activeId) || views[0];

  const compareRows = views.map((v) => ({
    key: v.id,
    label: v.compareLabel || v.title,
    withinPct: v.withinSlaPct,
    withinCount: v.withinCount,
    outsideCount: v.outsideCount,
  }));

  return (
    <section id={id} className="analytics-section">
      <div className="analytics-section-header">
        <h2 className="analytics-section-heading">{title}</h2>
        <span className="analytics-section-caption">Approval · PO · Invoice</span>
      </div>

      <div className="analytics-section-card">
        <h4 className="analytics-section-title">SLA Performance at a Glance</h4>
        <TimelinessCompareBars rows={compareRows} activeKey={activeId} onSelectRow={setActiveId} />

        <div className="tl-tab-strip" role="tablist">
          {views.map((v) => (
            <button
              key={v.id}
              id={v.id}
              type="button"
              role="tab"
              aria-selected={v.id === activeId}
              className={`tl-tab${v.id === activeId ? " tl-tab-active" : ""}`}
              onClick={() => setActiveId(v.id)}
            >
              {v.compareLabel || v.title}
            </button>
          ))}
        </div>
      </div>

      <div className="analytics-section-card">
        <WarningBanner warning={active.data?.warning} />
        {active.error && <div className="overview-card-error">Failed to load {active.title}: {active.error}</div>}
        {active.loading && !active.data && <div className="overview-card-loading">Loading…</div>}

        {active.data && (
          <>
            <div className="tl-detail-header">
              <h3 className="tl-detail-title">{active.title}</h3>
              <span className="analytics-section-caption">
                Avg {active.avgDays !== null ? Number(active.avgDays).toFixed(1) : "–"}d
                {active.oldestFieldPaths && active.oldestPendingDays !== null
                  ? ` · Oldest ${formatCompact(active.oldestPendingDays)}d`
                  : ""}
                {` · ${active.matrix.length} ${active.actorLabel.toLowerCase()} rows`}
              </span>
            </div>

            <div className="stat-tile-row">
              <StatTile label="Avg Days" value={active.avgDays !== null ? Number(active.avgDays).toFixed(1) : "–"} />
              {active.oldestFieldPaths && (
                <StatTile
                  label="Oldest Pending (Days)"
                  value={active.oldestPendingDays !== null ? formatCompact(active.oldestPendingDays) : "–"}
                  status={
                    active.oldestPendingDays !== null &&
                    active.thresholdDays &&
                    Number(active.oldestPendingDays) > active.thresholdDays * 3
                      ? "critical"
                      : undefined
                  }
                />
              )}
            </div>

            <h4 className="analytics-section-title">Avg Days by {active.actorLabel}</h4>
            <HorizontalBarChart
              items={active.chartItems}
              valueFormatter={(val) => `${Number(val).toFixed(1)}d`}
              maxItems={15}
              threshold={active.thresholdDays ? { value: active.thresholdDays, label: `SLA: ${active.thresholdDays}d` } : undefined}
            />

            <details className="analytics-collapsible" open={active.tableData.length <= 8}>
              <summary>Full breakdown — {active.tableData.length} rows</summary>
              <McTable data={active.tableData} datakey="_id" zebrastripes truncate fit="small" columns={active.columns} />
            </details>
          </>
        )}
      </div>
    </section>
  );
};

export default TimelinessComparisonSection;
